// ============================================================================
// PairsForm — Hook badges de menu (files de travail en attente)
// ============================================================================
// Audit UX 2026-07-05 (QW2, UX-REDESIGN §13.3) : le menu est un indicateur
// d'état — badge sur toute entrée qui est une file de travail. Poll 60 s,
// silencieux.
//
// Deux périmètres distincts :
//   • files de travail Cecodev (candidatures, demandes de création) → réservées
//     aux profils Cecodev pilote+ (endpoint gaté requirePiloteCecodev) ;
//   • badge « actions bloquantes » du Tableau de bord (D2) → tout pilote+ (de
//     n'importe quelle org) : nb d'actions cockpit BLOCKING **hors masquées**
//     (déjà filtré des signaux écartés côté serveur — cf. D1).
//
// ⚠️ Corrigé le 2026-08-04 : le badge ne comptait que le cockpit PROJET
// (`/projets/cockpits-orga`) et ignorait le cockpit ORGANISATION. Or c'est là
// que vivent les Critiques les plus coûteux — `FC-4` (argent animateur bloqué),
// `PL-9` (groupe confirmé sous le seuil), `AP-3` (document en quarantaine). Un
// pilote pouvait donc avoir trois actions Critiques et un menu muet.
// ============================================================================

import { useState, useEffect, useRef } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

interface MenuBadges {
  candidaturesAnimateurs: number;
  actionsBloquantes: number;
  /** CRM C1.2 : affaires actives sans prochaine action ou en retard d'échéance. */
  affairesATraiter: number;
  /** S2 Chargé d'affaire : nb de comptes clients affectés (gate l'entrée
   *  de menu « Mes comptes clients » — retour FX 2026-08-13). */
  mesComptesClients: number;
  /** Pièces animateur déposées et en attente de validation (retour FX
   *  2026-08-19 : « je n'ai toujours pas de signal compteur à côté de
   *  Validation documents animateurs »). Même périmètre que la file
   *  qu'elle ouvre — non org-scopée, gardée `requirePiloteCecodev`. */
  documentsAValider: number;
  /** Propositions de groupes Inter en attente de validation (retour FX
   *  2026-08-19). Comptées sur la MARKETPLACE, pas sur l'org active : le badge
   *  doit alerter le pilote avant qu'il n'y bascule. Cf. la note P8 sur
   *  `GET /plateforme/propositions/compteurs`. */
  propositionsAValider: number;
}

const VIDE: MenuBadges = { candidaturesAnimateurs: 0, actionsBloquantes: 0, affairesATraiter: 0, mesComptesClients: 0, documentsAValider: 0, propositionsAValider: 0 };

interface CockpitSummaryLite {
  totaux?: { blocking?: number };
}

interface OrgCockpitActionLite {
  priorite?: string;
}

export function useMenuBadges(): MenuBadges {
  const { user } = useAuth();
  const [badges, setBadges] = useState<MenuBadges>(VIDE);
  const pollRef = useRef<ReturnType<typeof setInterval>>();

  const estPilotePlus =
    !!user && (user.roles.includes("SUPER_ADMIN") || user.roles.includes("PILOTE"));

  const estCecodevAdmin =
    !!user &&
    (user.roles.includes("SUPER_ADMIN") ||
      (user.roles.includes("PILOTE") &&
        (user.organisationType === "CECODEV" || user.organisationType === "INTER")));

  // S2 Chargé d'affaire : un animateur peut porter des comptes clients — le
  // menu « Mes comptes clients » n'apparaît que si la liste est non vide.
  const estAnimateurRole =
    !!user && (user.estAnimateur || user.roles.includes("ANIMATEUR"));

  useEffect(() => {
    if (!estPilotePlus && !estAnimateurRole) {
      setBadges(VIDE);
      return;
    }

    const fetchCounts = async () => {
      // (a) actions bloquantes du cockpit (tout pilote+) — les DEUX niveaux :
      // projet ET organisation. Les deux appels sont indépendants : l'échec de
      // l'un ne doit pas faire disparaître le badge de l'autre.
      let actionsBloquantes = 0;
      if (estPilotePlus) {
        try {
          const res = await api.get<CockpitSummaryLite[]>("/projets/cockpits-orga");
          actionsBloquantes += (res.data ?? []).reduce((n, s) => n + (s.totaux?.blocking ?? 0), 0);
        } catch {
          /* silencieux */
        }
        try {
          const res = await api.get<OrgCockpitActionLite[]>("/organisations/cockpit");
          actionsBloquantes += (res.data ?? []).filter((a) => a.priorite === "BLOCKING").length;
        } catch {
          /* silencieux */
        }
      }

      // (b) files de travail Cecodev (pilote+ Cecodev/Inter uniquement)
      let candidaturesAnimateurs = 0;
      let affairesATraiter = 0;
      let documentsAValider = 0;
      let propositionsAValider = 0;
      if (estCecodevAdmin) {
        // Même source que la page de validation : `/queue/pending` n'est pas
        // org-scopée (gardée requirePiloteCecodev) — le compteur dit donc
        // exactement ce que la file affichera, sans écart possible.
        try {
          // Deux populations, une seule file (et un seul badge) : les
          // documents SOUMIS + les contrats-cadres signés en attente de
          // contre-signature Cecodev (retour FX 2026-09-14).
          const [resDocs, resContrats] = await Promise.all([
            api.get<unknown[]>("/animateur-documents/queue/pending"),
            api.get<unknown[]>("/animateur-documents/queue/contrats-a-contresigner"),
          ]);
          documentsAValider = (resDocs.data ?? []).length + (resContrats.data ?? []).length;
        } catch {
          /* silencieux */
        }
        try {
          const res = await api.get<{ aValider?: number }>("/plateforme/propositions/compteurs");
          propositionsAValider = res.data?.aValider ?? 0;
        } catch {
          /* silencieux */
        }
        try {
          const res = await api.get("/inscription-requests/pending-counts");
          candidaturesAnimateurs = res.data.candidaturesAnimateurs || 0;
        } catch {
          /* silencieux */
        }
        // CRM C1.2 — la clé CRM.* n'est accordée qu'au pilote CECODEV : un
        // pilote INTER reçoit 403, avalé en silence (badge à 0).
        try {
          const res = await api.get("/crm/affaires/compteurs");
          affairesATraiter = res.data.aTraiter || 0;
        } catch {
          /* silencieux */
        }
      }

      // (c) comptes clients affectés (Chargé d'affaire) — gate du menu
      // « Mes comptes clients ».
      let mesComptesClients = 0;
      if (estAnimateurRole) {
        try {
          const res = await api.get("/crm/mes-comptes");
          mesComptesClients = (res.data?.comptes ?? []).length;
        } catch {
          /* silencieux */
        }
      }

      setBadges({ candidaturesAnimateurs, actionsBloquantes, affairesATraiter, mesComptesClients, documentsAValider, propositionsAValider });
    };

    fetchCounts();
    pollRef.current = setInterval(fetchCounts, 60_000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [estPilotePlus, estCecodevAdmin, estAnimateurRole]);

  return badges;
}
